import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Inject } from '@angular/core';
import { GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import { AppFeatureKey, AppRoleKey, SensitiveDataTypeKey } from '@nk/data-entities-lib';
import { deserialize, includes, retryIf } from '@nk/utils-lib';
import posthog from 'posthog-js';
import { catchError, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RequestOptions } from '../utils/api-client.utils';

function isHttpErrorResponse(item: unknown): item is HttpErrorResponse {
  return (
    !!item &&
    typeof item === 'object' &&
    (item as HttpErrorResponse).name === 'HttpErrorResponse' &&
    item.hasOwnProperty('status') &&
    item.hasOwnProperty('message')
  );
}

function logErrors(
  error: unknown,
  endPoint: string,
  options?: RequestOptions,
  featureKeys?: AppFeatureKey[],
  roleKeys?: AppRoleKey[],
  sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
): void {
  if (isHttpErrorResponse(error)) {
    // Log error to PostHog
    posthog.capture('$httpError', {
      message: error.message || error.toString(),
      name: error.name,
      status: error.status,
      url: endPoint,
      method: 'GET',
      userUrl: window.location.href,
      user_options: options,
      user_feature_keys: featureKeys,
      user_role_keys: roleKeys,
      user_sensitive_data_type_keys: sensitiveDataTypeKeys,
      errorObject: error,
    });
  } else {
    // Log error to PostHog
    posthog.capture('$unknownError', {
      // @ts-ignore
      message: error['message'] ? error['message'] : 'unknown',
      url: endPoint,
      method: 'GET',
      userUrl: window.location.href,
      user_options: options,
      user_feature_keys: featureKeys,
      user_role_keys: roleKeys,
      user_sensitive_data_type_keys: sensitiveDataTypeKeys,
      errorObject: error,
    });
  }
}

export interface ApiClient {
  /**
   * GET postForm
   * @param {string} endPoint it doesn't need / in front of the end point
   * @param {RequestOptions} options options of the postForm like headers, body, etc.
   * @param {AppFeatureKey} featureKeys - features the user claims to have access to
   * @param {AppRoleKey} roleKeys - roles that user claims to be
   * @param {SensitiveDataTypeKey} sensitiveDataTypeKeys - sensitive data the user claims to have access to
   * @param retryFn - a retry condition function, when request fails and if true is returned then it will retry request
   * @returns {Observable<T>}
   */
  get<T>(
    endPoint: string,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
    retryFn?: () => boolean,
  ): Observable<T>;

  /**
   * POST postForm
   * @param {string} endPoint end point of the api
   * @param {any} body body of the postForm.
   * @param {RequestOptions} options options of the postForm like headers, body, etc.
   * @param {AppFeatureKey} featureKeys - features the user claims to have access to
   * @param {AppRoleKey} roleKeys - roles that user claims to be
   * @param {SensitiveDataTypeKey} sensitiveDataTypeKeys - sensitive data the user claims to have access to
   * @returns {Observable<T>}
   */
  post<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T>;

  /**
   * PUT postForm
   * @param {string} endPoint end point of the api
   * @param {any} body body of the postForm.
   * @param {RequestOptions} options options of the postForm like headers, body, etc.
   * @param {AppFeatureKey} featureKeys - features the user claims to have access to
   * @param {AppRoleKey} roleKeys - roles that user claims to be
   * @param {SensitiveDataTypeKey} sensitiveDataTypeKeys - sensitive data the user claims to have access to
   * @returns {Observable<T>}
   */
  put<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T>;

  /**
   * PATCH postForm
   * @param {string} endPoint end point of the api
   * @param {any} body body of the postForm.
   * @param {RequestOptions} options options of the postForm like headers, body, etc.
   * @param {AppFeatureKey} featureKeys - features the user claims to have access to
   * @param {AppRoleKey} roleKeys - roles that user claims to be
   * @param {SensitiveDataTypeKey} sensitiveDataTypeKeys - sensitive data the user claims to have access to
   * @returns {Observable<T>}
   */
  patch<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T>;

  /**
   * DELETE postForm
   * @param {string} endPoint end point of the api
   * @param {RequestOptions} options options of the postForm like headers, body, etc.
   * @param {AppFeatureKey} featureKeys - features the user claims to have access to
   * @param {AppRoleKey} roleKeys - roles that user claims to be
   * @param {SensitiveDataTypeKey} sensitiveDataTypeKeys - sensitive data the user claims to have access to
   * @returns {Observable<T>}
   */
  delete<T>(
    endPoint: string,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T>;
}

export interface FullApiClient extends ApiClient {
  postForm<T>(endPoint: string, formData: FormData, options?: RequestOptions): Observable<HttpEvent<T>>;

  postFile<T>(endPoint: string, file: File, options?: RequestOptions): Observable<HttpEvent<T>>;

  getBlob(endPoint: string, options?: RequestOptions): Observable<Blob>;
}

export abstract class ApiClientBase implements FullApiClient {
  public static defaultVersion = 1;

  public rootUrl = '/';

  public get endPointPrefix(): string {
    return this.rootUrl + 'v1';
  }

  protected constructor(
    protected http: HttpClient,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) protected authService: GenericAuthService,
    protected version = ApiClientBase.defaultVersion,
  ) {}

  private _applyApplicationHeaders(
    options?: RequestOptions,
    featureKeys: AppFeatureKey[] = [AppFeatureKey.base],
    roleKeys: AppRoleKey[] = [],
    sensitiveDataTypeKeys: SensitiveDataTypeKey[] = [],
  ): RequestOptions {
    if (options) {
      options = { ...options };
    } else {
      options = new RequestOptions();
    }
    if (!options.headers) {
      options.headers = new HttpHeaders();
    }
    if (window?.location) {
      options.headers = options.headers.append('Nk-Referer', window.location.toString());
    }
    if (featureKeys && featureKeys.length > 0) {
      featureKeys = featureKeys.sort();
      options.headers = options.headers.append('Nk-Feature-Keys', featureKeys.join(','));
    }
    if (roleKeys && roleKeys.length > 0) {
      roleKeys = roleKeys.sort();
      options.headers = options.headers.append('Nk-Role-Keys', roleKeys.join(','));
    }
    if (sensitiveDataTypeKeys && sensitiveDataTypeKeys.length > 0) {
      sensitiveDataTypeKeys = sensitiveDataTypeKeys.sort();
      options.headers = options.headers.append('Nk-Sensitive-Data-Type-Keys', sensitiveDataTypeKeys.join(','));
    }
    return options;
  }

  public postForm<T>(endPoint: string, formData: FormData, options?: RequestOptions): Observable<HttpEvent<T>> {
    // Consider merging with postFile()
    endPoint = this._prepUrl(endPoint);
    options = this._applyApplicationHeaders(options, []);
    const req = new HttpRequest('POST', endPoint, formData, options);
    return this.http.request(req);
  }

  public postFile<T>(endPoint: string, file: File, options?: RequestOptions): Observable<HttpEvent<T>> {
    // Consider merging with postForm()
    endPoint = this._prepUrl(endPoint);
    options = this._applyApplicationHeaders(options, []);
    const req = new HttpRequest('POST', endPoint, file, options);
    return this.http.request(req);
  }

  public getBlob(endPoint: string, options?: RequestOptions, retryFn?: () => boolean): Observable<Blob> {
    endPoint = this._prepUrl(endPoint);
    options = this._applyApplicationHeaders(options, []);
    const relevantStatusCodes = [
      500, // If server is down or internet connection is lost, it will give 500
      0, // Rejected requests or cors errors can give status code 0.
    ];
    const defaultRetryFn = (error: unknown): boolean =>
      error instanceof HttpErrorResponse && includes(relevantStatusCodes, error.status);
    return this.http.request('GET', endPoint, { ...options, responseType: 'blob' }).pipe(
      catchError((error: unknown) => {
        logErrors(error, endPoint, options);
        throw error;
      }),
      retryIf(retryFn ?? defaultRetryFn),
    );
  }

  public get<T>(
    endPoint: string,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
    retryFn?: () => boolean,
  ): Observable<T> {
    options = this._applyApplicationHeaders(options, featureKeys, roleKeys, sensitiveDataTypeKeys);
    const relevantStatusCodes = [
      500, // If server is down or internet connection is lost, it will give 500
      0, // Rejected requests or cors errors can give status code 0.
    ];
    const defaultRetryFn = (error: unknown): boolean =>
      error instanceof HttpErrorResponse && includes(relevantStatusCodes, error.status);

    return this.http.get<T>(this._prepUrl(endPoint), options).pipe(
      retryIf(retryFn ?? defaultRetryFn),
      catchError((error: unknown) => {
        logErrors(error, endPoint, options, featureKeys, roleKeys, sensitiveDataTypeKeys);
        throw error;
      }),
      map((payload) => deserialize(payload)),
    );
  }

  public post<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    options = this._applyApplicationHeaders(options, featureKeys, roleKeys, sensitiveDataTypeKeys);
    return this.http.post<T>(this._prepUrl(endPoint), body, options).pipe(
      catchError((error: unknown) => {
        logErrors(error, endPoint, options, featureKeys, roleKeys, sensitiveDataTypeKeys);
        throw error;
      }),
      map((payload) => deserialize(payload)),
    );
  }

  public put<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    options = this._applyApplicationHeaders(options, featureKeys, roleKeys, sensitiveDataTypeKeys);
    return this.http.put<T>(this._prepUrl(endPoint), body, options).pipe(
      catchError((error: unknown) => {
        logErrors(error, endPoint, options, featureKeys, roleKeys, sensitiveDataTypeKeys);
        throw error;
      }),
      map((payload) => deserialize(payload)),
    );
  }

  public patch<T>(
    endPoint: string,
    body: unknown,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    options = this._applyApplicationHeaders(options, featureKeys, roleKeys, sensitiveDataTypeKeys);
    return this.http.patch<T>(this._prepUrl(endPoint), body, options).pipe(
      catchError((error: unknown) => {
        logErrors(error, endPoint, options, featureKeys, roleKeys, sensitiveDataTypeKeys);
        throw error;
      }),
      map((payload) => deserialize(payload)),
    );
  }

  public delete<T>(
    endPoint: string,
    options?: RequestOptions,
    featureKeys?: AppFeatureKey[],
    roleKeys?: AppRoleKey[],
    sensitiveDataTypeKeys?: SensitiveDataTypeKey[],
  ): Observable<T> {
    options = this._applyApplicationHeaders(options, featureKeys, roleKeys, sensitiveDataTypeKeys);
    return this.http.delete<T>(this._prepUrl(endPoint), options).pipe(
      catchError((error: unknown) => {
        logErrors(error, endPoint, options, featureKeys, roleKeys, sensitiveDataTypeKeys);
        throw error;
      }),
      map((payload) => deserialize(payload)),
    );
  }

  private _prepUrl(endPoint: string): string {
    if (endPoint.startsWith('/')) {
      endPoint = endPoint.slice(1);
    }
    let endPointPrefix = this.endPointPrefix;
    if (this.endPointPrefix.endsWith('/')) {
      endPointPrefix = endPointPrefix.slice(0, -1);
    }
    return `${endPointPrefix}/${endPoint}`;
  }
}
